import { hasActivePaidSubscription } from './generationBillingAccess';
import { type ReactElement, useState } from 'react';
import type {
  BillingEntitlementCode,
  BillingPlan,
  BillingPlanBadge,
  SubscriptionBillingCadence,
  WorkspaceBillingContract,
} from '@nest-react/domain';
import type { YooKassaCheckoutContract } from '../../repositories/billingRepository';
import { CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { cn } from '~/lib/utils';
import { CreditLabel } from '~/components/layout/CreditLabel';
import type { BillingDialog } from './billingDialogState';
import { InsufficientCreditsNotice } from './InsufficientCreditsNotice';

type PriceCadence = 'monthly' | 'yearly';

const planNameKeys: Record<BillingPlan, string> = {
  FREE: 'planNames.FREE',
  STANDARD: 'planNames.STANDARD',
  PRO: 'planNames.PRO',
  MAX: 'planNames.MAX',
  ENTERPRISE: 'planNames.ENTERPRISE',
};

const badgeKeys: Record<BillingPlanBadge, string> = {
  POPULAR: 'badges.POPULAR',
  BEST_VALUE: 'badges.BEST_VALUE',
};

const entitlementKeys: Record<BillingEntitlementCode, string> = {
  CORE_AGENT: 'entitlements.CORE_AGENT',
  IMAGE_AND_VIDEO_TOOLS: 'entitlements.IMAGE_AND_VIDEO_TOOLS',
  PRIVATE_ASSET_LIBRARY: 'entitlements.PRIVATE_ASSET_LIBRARY',
  ECONOMY_VIDEO_MODEL: 'entitlements.ECONOMY_VIDEO_MODEL',
  INCLUDES_FREE: 'entitlements.INCLUDES_FREE',
  ALL_PRODUCTION_MODELS: 'entitlements.ALL_PRODUCTION_MODELS',
  MONTHLY_CREDIT_REFRESH: 'entitlements.MONTHLY_CREDIT_REFRESH',
  CREDIT_PACK_PURCHASES: 'entitlements.CREDIT_PACK_PURCHASES',
  VIDEO_UP_TO_1080P: 'entitlements.VIDEO_UP_TO_1080P',
  INCLUDES_STANDARD: 'entitlements.INCLUDES_STANDARD',
  HIGHER_MONTHLY_GENERATION_ALLOWANCE:
    'entitlements.HIGHER_MONTHLY_GENERATION_ALLOWANCE',
  API_ACCESS: 'entitlements.API_ACCESS',
  WATERMARK_FREE_IMAGES: 'entitlements.WATERMARK_FREE_IMAGES',
  WATERMARK_FREE_VIDEOS: 'entitlements.WATERMARK_FREE_VIDEOS',
  WATERMARK_FREE_VIDEO_EDITOR_EXPORTS:
    'entitlements.WATERMARK_FREE_VIDEO_EDITOR_EXPORTS',
  VIDEO_UP_TO_4K: 'entitlements.VIDEO_UP_TO_4K',
  INCLUDES_PRO: 'entitlements.INCLUDES_PRO',
  HIGHEST_MONTHLY_GENERATION_ALLOWANCE:
    'entitlements.HIGHEST_MONTHLY_GENERATION_ALLOWANCE',
  ONE_MONTH_CREDIT_ROLLOVER: 'entitlements.ONE_MONTH_CREDIT_ROLLOVER',
  INCLUDES_MAX: 'entitlements.INCLUDES_MAX',
  CUSTOM_CREDITS: 'entitlements.CUSTOM_CREDITS',
  CUSTOM_TEAM_SIZE: 'entitlements.CUSTOM_TEAM_SIZE',
  ENTERPRISE_SUPPORT: 'entitlements.ENTERPRISE_SUPPORT',
};

export const BillingDialogs = ({
  dialog,
  summary,
  onClose,
  onCheckout,
  onAbandonCheckout,
  onCreditCheckout,
  insufficientCredits,
  renewalRequired = false,
  watermarkUpgrade = false,
}: {
  dialog: BillingDialog | null;
  summary: WorkspaceBillingContract;
  onClose: () => void;
  onCheckout?: (
    plan: 'STANDARD' | 'PRO' | 'MAX',
    billingCadence: SubscriptionBillingCadence
  ) => Promise<YooKassaCheckoutContract | null>;
  onAbandonCheckout?: (orderId: string) => Promise<{ status: string }>;
  onCreditCheckout?: () => Promise<void>;
  insufficientCredits?: { available?: string; required: string };
  renewalRequired?: boolean;
  watermarkUpgrade?: boolean;
}): ReactElement => {
  const { t, i18n } = useTranslation('billing');
  const locale = i18n.resolvedLanguage;
  const annualDiscountPercent =
    summary.offers.find(offer => offer.annualPrice)?.annualPrice
      ?.discountPercent ?? 0;
  const [cadence, setCadence] = useState<PriceCadence>('monthly');
  const [checkoutPlan, setCheckoutPlan] = useState<BillingPlan | null>(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [pendingCheckout, setPendingCheckout] =
    useState<YooKassaCheckoutContract | null>(null);
  const [abandoningCheckout, setAbandoningCheckout] = useState(false);
  const [creditCheckoutLoading, setCreditCheckoutLoading] = useState(false);
  const activePaidTerm = hasActivePaidSubscription(summary.subscription);
  const annualCheckoutRestricted = cadence === 'yearly' && activePaidTerm;
  const checkoutRestricted =
    annualCheckoutRestricted ||
    (summary.subscription.billingCadence === 'YEAR' && activePaidTerm);
  const formatCurrency = (amount: string, currency: string): string =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  const formatNumber = (value: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
      Number(value)
    );

  return (
    <Dialog open={dialog !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent
        aria-label={
          dialog === 'credits'
            ? t('getCredits')
            : renewalRequired
              ? t('renewYourPlan')
              : t('upgradeYourPlan')
        }
        className={cn(
          'max-h-[min(92vh,58rem)] overscroll-contain overflow-y-auto',
          dialog === 'credits' ? 'sm:max-w-lg' : 'sm:max-w-[min(96vw,90rem)]'
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {dialog === 'credits'
              ? t('getCredits')
              : renewalRequired
                ? t('renewYourPlan')
                : t('upgradeYourPlan')}
          </DialogTitle>
          <DialogDescription>
            {dialog === 'upgrade'
              ? watermarkUpgrade
                ? t('watermarkUpgradeDescription')
                : summary.workspace.dataRegion === 'RU'
                  ? t('secureCheckout')
                  : t('displayOnly')
              : summary.workspace.dataRegion === 'RU' &&
                  summary.subscription.plan !== 'FREE'
                ? t('creditPackDescription')
                : t('creditPackRequiresPlan')}
          </DialogDescription>
        </DialogHeader>
        {dialog === 'upgrade' && renewalRequired ? (
          <div
            role="status"
            className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-3 text-sm leading-6 text-foreground"
          >
            <p className="font-semibold">{t('planExpiredTitle')}</p>
            <p>{t('planExpiredDescription')}</p>
          </div>
        ) : null}
        {dialog === 'upgrade' && insufficientCredits ? (
          <div
            aria-live="polite"
            className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-3 text-sm leading-6 text-foreground"
          >
            <p className="font-semibold">{t('insufficientCreditsTitle')}</p>
            <InsufficientCreditsNotice
              available={insufficientCredits.available}
              locale={locale}
              required={insufficientCredits.required}
            />
          </div>
        ) : null}
        {dialog === 'upgrade' ? (
          <div className="grid gap-5 pt-1">
            {pendingCheckout?.pendingOrder ? (
              <section
                aria-live="polite"
                className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4"
              >
                <p className="text-sm font-semibold text-foreground">
                  {t('pendingPlanPayment', {
                    plan: pendingCheckout.pendingOrder.plan,
                    cadence:
                      pendingCheckout.pendingOrder.billingCadence === 'YEAR'
                        ? t('annualCadence')
                        : t('monthlyCadence'),
                    amount: formatCurrency(
                      pendingCheckout.pendingOrder.amount,
                      pendingCheckout.pendingOrder.currency
                    ),
                  })}
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <button
                    className="min-h-11 touch-manipulation rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!pendingCheckout.confirmationUrl}
                    onClick={() => {
                      if (pendingCheckout.confirmationUrl)
                        window.location.assign(pendingCheckout.confirmationUrl);
                    }}
                    type="button"
                  >
                    {t('continuePendingPayment')}
                  </button>
                  <button
                    className="min-h-11 touch-manipulation rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={abandoningCheckout}
                    onClick={() => {
                      if (!onAbandonCheckout) return;
                      setAbandoningCheckout(true);
                      setCheckoutError('');
                      void onAbandonCheckout(pendingCheckout.orderId)
                        .then(result => {
                          if (result.status !== 'CANCELED') {
                            throw new Error('Payment was not canceled');
                          }
                          setPendingCheckout(null);
                        })
                        .catch(() =>
                          setCheckoutError(t('abandonPendingPaymentError'))
                        )
                        .finally(() => setAbandoningCheckout(false));
                    }}
                    type="button"
                  >
                    {abandoningCheckout
                      ? t('abandoningPendingPayment')
                      : t('abandonPendingPayment')}
                  </button>
                </div>
              </section>
            ) : null}
            <div
              aria-label={t('billingCadence')}
              className="mx-auto flex rounded-full border border-border bg-surface p-1 focus-within:ring-1 focus-within:ring-ring"
              role="group"
            >
              <button
                aria-pressed={cadence === 'monthly'}
                className={cn(
                  'min-h-11 touch-manipulation rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  cadence === 'monthly'
                    ? 'bg-foreground text-background'
                    : 'text-muted hover:text-foreground'
                )}
                onClick={() => setCadence('monthly')}
                type="button"
              >
                {t('monthly')}
              </button>
              <button
                aria-pressed={cadence === 'yearly'}
                className={cn(
                  'min-h-11 touch-manipulation rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  cadence === 'yearly'
                    ? 'bg-foreground text-background'
                    : 'text-muted hover:text-foreground'
                )}
                onClick={() => setCadence('yearly')}
                type="button"
              >
                {t('yearlyDiscount', { discount: annualDiscountPercent })}
              </button>
            </div>

            {checkoutRestricted ? (
              <p aria-live="polite" className="text-center text-sm text-muted">
                {summary.subscription.billingCadence === 'YEAR' &&
                activePaidTerm
                  ? t('annualTermLocked')
                  : t('annualAfterCurrentTerm')}
              </p>
            ) : null}

            <div className="overflow-x-auto pb-2">
              {checkoutError ? (
                <p aria-live="polite" className="mb-3 text-sm text-destructive">
                  {checkoutError}
                </p>
              ) : null}
              <div className="grid gap-4 md:grid-flow-col md:auto-cols-[minmax(15rem,1fr)]">
                {summary.offers.map(offer => {
                  const current =
                    offer.plan === summary.subscription.plan &&
                    (offer.plan === 'FREE' ||
                      offer.plan === 'ENTERPRISE' ||
                      activePaidTerm);
                  const recommended = watermarkUpgrade && offer.plan === 'PRO';
                  const annual =
                    cadence === 'yearly' ? offer.annualPrice : null;
                  const displayPrice = annual
                    ? {
                        amount: annual.effectiveMonthlyAmount,
                        currency: annual.currency,
                      }
                    : offer.monthlyPrice;
                  return (
                    <article
                      className={cn(
                        'relative flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border bg-background p-5',
                        (watermarkUpgrade ? recommended : current)
                          ? 'border-primary ring-1 ring-primary/30'
                          : 'border-border',
                        !watermarkUpgrade &&
                          offer.badge === 'POPULAR' &&
                          'border-amber-300/80',
                        offer.badge === 'BEST_VALUE' && 'border-sky-300/80'
                      )}
                      data-testid="billing-plan-card"
                      key={offer.plan}
                    >
                      <div
                        className={cn(
                          '-mx-5 -mt-5 mb-4 flex h-10 items-center px-5 text-xs font-bold tracking-[0.12em] uppercase',
                          offer.badge === 'POPULAR' &&
                            'bg-amber-200 text-amber-950',
                          offer.badge === 'BEST_VALUE' &&
                            'bg-sky-200 text-sky-950'
                        )}
                        data-testid="billing-plan-badge-slot"
                      >
                        {offer.badge ? t(badgeKeys[offer.badge]) : null}
                      </div>
                      <div className="flex min-h-7 items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-foreground">
                          {t(planNameKeys[offer.plan])}
                        </h3>
                        {current ? (
                          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                            {t('currentPlan')}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-6 min-h-24">
                        {offer.custom || !displayPrice ? (
                          <p className="text-2xl font-semibold text-foreground">
                            {t('customPricing')}
                          </p>
                        ) : (
                          <>
                            <p className="text-3xl font-semibold tracking-tight text-foreground tabular-nums">
                              {formatCurrency(
                                displayPrice.amount,
                                displayPrice.currency
                              )}
                            </p>
                            <p className="mt-1 text-sm text-muted">
                              {offer.creditCadence === 'ONCE'
                                ? t('once')
                                : t('perMonth')}
                            </p>
                            {annual ? (
                              <p className="mt-2 text-xs leading-5 text-muted">
                                {t('billedAnnually')}
                                <br />
                                {t('annualTotal', {
                                  price: formatCurrency(
                                    annual.totalAmount,
                                    annual.currency
                                  ),
                                })}
                              </p>
                            ) : null}
                          </>
                        )}
                      </div>

                      <p className="flex flex-wrap items-center gap-2 border-b border-border pb-5 text-sm font-medium text-foreground">
                        {offer.includedCredits === null ? (
                          t('customCredits')
                        ) : (
                          <>
                            <span>{t('includedCreditsLabel')}</span>
                            <CreditLabel
                              balance={offer.includedCredits}
                              locale={locale ?? 'en'}
                              variant="inline"
                            />
                          </>
                        )}
                      </p>
                      <ul className="mt-5 grid flex-1 content-start gap-3">
                        {offer.entitlements.map(code => (
                          <li
                            className="flex gap-2.5 text-sm leading-5 text-foreground"
                            key={code}
                          >
                            <CheckIcon
                              aria-hidden="true"
                              className="mt-0.5 size-4 shrink-0 text-primary"
                            />
                            <span className="min-w-0 break-words">
                              {t(entitlementKeys[code])}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {summary.workspace.dataRegion === 'RU' &&
                      !current &&
                      !offer.custom &&
                      offer.plan !== 'FREE' ? (
                        <button
                          className="mt-6 min-h-11 w-full touch-manipulation rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={checkoutRestricted || checkoutPlan !== null}
                          onClick={() => {
                            if (!onCheckout) return;
                            const plan = offer.plan as
                              'STANDARD' | 'PRO' | 'MAX';
                            setCheckoutPlan(plan);
                            setCheckoutError('');
                            void onCheckout(
                              plan,
                              cadence === 'yearly' ? 'YEAR' : 'MONTH'
                            )
                              .then(result => {
                                if (result?.action === 'RESOLVE_PENDING')
                                  setPendingCheckout(result);
                              })
                              .catch(reason => {
                                setCheckoutError(
                                  reason instanceof Error
                                    ? reason.message
                                    : t('checkoutError')
                                );
                              })
                              .finally(() => setCheckoutPlan(null));
                          }}
                          type="button"
                        >
                          {checkoutPlan === offer.plan
                            ? t('openingCheckout')
                            : recommended
                              ? t('upgradeToPro')
                              : t('continueToPayment')}
                        </button>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        ) : dialog === 'credits' ? (
          <div className="grid gap-3 pt-1">
            {insufficientCredits ? (
              <div
                aria-live="polite"
                className="rounded-2xl border border-primary/25 bg-primary/10 px-4 py-3 text-sm leading-6 text-foreground"
              >
                <p className="font-semibold">{t('insufficientCreditsTitle')}</p>
                <InsufficientCreditsNotice
                  available={insufficientCredits.available}
                  locale={locale}
                  required={insufficientCredits.required}
                />
              </div>
            ) : null}
            <article className="overflow-hidden rounded-2xl border border-border bg-background">
              <div className="border-b border-border bg-primary/10 px-4 py-3">
                <p className="text-xs font-bold tracking-[0.12em] text-primary uppercase">
                  {t('oneTimeCreditPack')}
                </p>
                <div className="mt-1.5 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-2xl font-semibold tracking-tight text-foreground tabular-nums">
                      +{formatNumber('1000')}
                    </p>
                    <p className="mt-1 text-sm text-muted">{t('credits')}</p>
                  </div>
                  <p className="text-xl font-semibold text-foreground tabular-nums">
                    {formatCurrency('1290', 'RUB')}
                  </p>
                </div>
              </div>
              <div className="p-4">
                <ul className="grid gap-2 text-sm leading-6 text-muted">
                  <li>{t('creditPackNoRenewal')}</li>
                  <li>{t('creditPackPaidPlanOnly')}</li>
                </ul>
                {checkoutError ? (
                  <p
                    aria-live="polite"
                    className="mt-4 text-sm text-destructive"
                  >
                    {checkoutError}
                  </p>
                ) : null}
                <button
                  className="mt-4 min-h-11 w-full touch-manipulation rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    creditCheckoutLoading ||
                    summary.workspace.dataRegion !== 'RU' ||
                    summary.subscription.plan === 'FREE' ||
                    !onCreditCheckout
                  }
                  onClick={() => {
                    if (!onCreditCheckout) return;
                    setCreditCheckoutLoading(true);
                    setCheckoutError('');
                    void onCreditCheckout().catch(reason => {
                      setCreditCheckoutLoading(false);
                      setCheckoutError(
                        reason instanceof Error
                          ? reason.message
                          : t('checkoutError')
                      );
                    });
                  }}
                  type="button"
                >
                  {creditCheckoutLoading
                    ? t('openingCheckout')
                    : t('buyCreditPack')}
                </button>
              </div>
            </article>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
